import { useSyncExternalStore, type ReactNode } from 'react';
import type { ToastVariant, ToastDismiss } from '../components/Toast';

// Central toast store (Issue #194). One place every in-app notice pushes to,
// rendered by ONE <GlobalToasts> host — so StatusToast / UpdatePrompt / paFlash
// all share a single top-right stack instead of overlapping fixed containers,
// and all look + behave identically (slide from the right, configurable dismiss).

export interface ToastEntry {
  id: number;
  variant?: ToastVariant;          // info | error | success
  dismiss?: ToastDismiss;          // auto | manual | persist (default per variant)
  autoHideMs?: number;
  icon?: ReactNode | null;
  title?: ReactNode;
  content?: ReactNode;
  onClick?: () => void;
  /** Called when the toast is dismissed (X click or auto-hide) — lets the pusher
   *  persist "seen" state (e.g. update-notice per version, #216). */
  onClose?: () => void;
  /** Collapse re-pushes of the same logical notice (e.g. a re-dispatched issue). */
  dedupKey?: string;
}

let entries: ToastEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  entries = [...entries];               // new ref so useSyncExternalStore re-renders
  listeners.forEach((l) => l());
}

/** Push a toast. Returns its id, or -1 if a live toast with the same dedupKey exists. */
export function pushToast(entry: Omit<ToastEntry, 'id'>): number {
  if (entry.dedupKey && entries.some((e) => e.dedupKey === entry.dedupKey)) return -1;
  const id = nextId++;
  entries = [...entries, { ...entry, id }];
  listeners.forEach((l) => l());
  return id;
}

export function dismissToast(id: number) {
  const next = entries.filter((e) => e.id !== id);
  if (next.length !== entries.length) { entries = next; listeners.forEach((l) => l()); }
}

/** Remove any live toast(s) matching a dedupKey (e.g. clear a resolved alert). */
export function dismissToastByKey(dedupKey: string) {
  const next = entries.filter((e) => e.dedupKey !== dedupKey);
  if (next.length !== entries.length) { entries = next; listeners.forEach((l) => l()); }
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function useToasts(): ToastEntry[] {
  return useSyncExternalStore(subscribe, () => entries, () => entries);
}

void emit; // reserved for future batch updates
